import React from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore, isAfter, isSameDay } from 'date-fns';

import styles from './styles/MyBillsTable.module.scss';

import MyBillsTableRow from './MyBillsTableRow';

// Types.
import { myBillsTableTypes, tableSorterConfig, tableSorter, InvoiceAkamon } from '../../types/billing';

// https://www.smashingmagazine.com/2020/03/sortable-tables-react/

const useSortableData = (items: InvoiceAkamon[]): tableSorter => {
  const [sortConfig, setSortConfig] = React.useState<tableSorterConfig>();

  const sortedItems = React.useMemo((): InvoiceAkamon[] => {
    const sortableItems = [...items];
    if (sortConfig !== undefined) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: keyof InvoiceAkamon): void => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const MyBillsTable: React.FunctionComponent<myBillsTableTypes> = (props: myBillsTableTypes) => {
  const { t } = useTranslation();

  const { invoices, postpone, desiredPdf, setDesiredPdf, mostRecentInvoiceDate, mostRecentInvoiceNumber, startDate, endDate } = props;
  const { items, requestSort, sortConfig } = useSortableData(invoices.sort((a, b) => (a.dueDate < b.dueDate ? 1 : -1)));

  const getClassNamesFor = (name: keyof InvoiceAkamon): string | undefined => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <>
      <div className={`table-responsive ${styles.tableWrapper}`}>
        <div className={styles.table}>
          <span className="sr-only">{t(`billing:billing:sortHeading`)}</span>
          <div className={styles.header}>
            <div>
              <button onClick={(): void => requestSort('status')} className={sortConfig && sortConfig.key === 'status' ? styles.active : ''}>
                {t('billing:billing.sortBillState')}
                <span className={styles[getClassNamesFor('status') ?? '']} />
              </button>
            </div>
            <div>
              <button
                onClick={(): void => requestSort('totalWithVat')}
                className={sortConfig && sortConfig.key === 'totalWithVat' ? styles.active : ''}
              >
                {t('billing:billing.sortBillSum')}
                <span className={styles[getClassNamesFor('totalWithVat') ?? '']} />
              </button>
            </div>
            <div>
              <button onClick={(): void => requestSort('dueDate')} className={sortConfig && sortConfig.key === 'dueDate' ? styles.active : ''}>
                {t('billing:billing.sortDueDate')}
                <span className={styles[getClassNamesFor('dueDate') ?? '']} />
              </button>
            </div>
            <div>
              <button
                onClick={(): void => requestSort('dateOfInvoice')}
                className={sortConfig && sortConfig.key === 'dateOfInvoice' ? styles.active : ''}
              >
                {t('billing:billing.sortInvoiceDate')}
                <span className={styles[getClassNamesFor('dateOfInvoice') ?? '']} />
              </button>
            </div>
            <div>
              <button onClick={(): void => requestSort('identifier')} className={sortConfig && sortConfig.key === 'identifier' ? styles.active : ''}>
                {t('billing:billing.sortBillNumber')}
                <span className={styles[getClassNamesFor('identifier') ?? '']} />
              </button>
            </div>
            <div>
              <span className={styles.tableHeading}>{t('billing:billing.sortPdf')}</span>
            </div>
            <div>
              <span className={styles.tableHeading}>{t('billing:billing.sortBarCode')}</span>
            </div>
          </div>
          {items &&
            items
              .filter((invoice) => {
                const filterStart = startDate
                  ? isSameDay(startDate, new Date(invoice.dueDate)) || isBefore(startDate, new Date(invoice.dueDate))
                  : true;
                const filterEnd = endDate ? isSameDay(endDate, new Date(invoice.dueDate)) || isAfter(endDate, new Date(invoice.dueDate)) : true;

                if (filterStart && filterEnd) {
                  return true;
                }
                return false;
              })
              .map((invoice) => (
                <MyBillsTableRow
                  key={invoice.identifier}
                  invoice={invoice}
                  postpone={postpone}
                  desiredPdf={desiredPdf}
                  setDesiredPdf={setDesiredPdf}
                  mostRecentInvoiceDate={mostRecentInvoiceDate}
                  mostRecentInvoiceNumber={mostRecentInvoiceNumber}
                />
              ))}
        </div>
      </div>
    </>
  );
};

export default MyBillsTable;
